import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import {
  productUsageReason,
  type CatalogProduct,
  type ProductUsage,
} from "@school-sales-ai/shared";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useDeleteInactiveProduct } from "@/hooks/useCatalog";
import { ProductMediaIndicator } from "./ProductMediaIndicator";
import { cyclesTypeLabel } from "./row-fields";

/** One row of the block, with what still points at the product. */
export type InactiveRow = {
  product: CatalogProduct & { id: string };
  usage: ProductUsage;
};

function isUnused(usage: ProductUsage): boolean {
  return Object.values(usage).every((n) => n === 0);
}

function formatDay(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString("es-MX", { day: "numeric", month: "short" });
}

function scheduleSummary(product: CatalogProduct, usesCycles: boolean): string | null {
  const schedule = product.schedule;
  if (!schedule) return null;
  if (schedule.type === "cycles") return cyclesTypeLabel(usesCycles);
  if (schedule.startDate && schedule.endDate) {
    return `${formatDay(schedule.startDate)} – ${formatDay(schedule.endDate)}`;
  }
  return "Sin fecha";
}

/**
 * Deactivated products, where they can be SEEN — before this, the X made a
 * product vanish and the only way back was retyping its exact name.
 *
 * A holding area, not an editor: a row shows what it is and what it still
 * carries (its media icons prove nothing was lost), and offers «Reactivar».
 * «Eliminar» only appears for a product nothing uses; otherwise the row says
 * what blocks it. The server re-checks on delete either way.
 */
export function InactiveProductsSection({
  rows,
  noun,
  disabled,
  onReactivate,
  usesCycles = true,
}: {
  rows: InactiveRow[];
  /** "Programas" (school shape) or "Productos" (flat list). */
  noun: string;
  disabled?: boolean;
  /** Names a `cycles` program «Por ciclo» or «Inscripción abierta», as the switch does. */
  usesCycles?: boolean;
  /** Saves at once; the row leaves this list when the server says so. */
  onReactivate: (product: CatalogProduct & { id: string }) => Promise<boolean>;
}): React.JSX.Element | null {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState<InactiveRow["product"] | null>(null);
  const [reactivating, setReactivating] = useState<string | null>(null);
  const deleteMutation = useDeleteInactiveProduct();

  if (rows.length === 0) return null;

  return (
    <section className="rounded-lg border border-ink-100 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-4 py-3 text-left"
      >
        {open ? (
          <ChevronDown size={14} className="text-ink-400" />
        ) : (
          <ChevronRight size={14} className="text-ink-400" />
        )}
        <h4 className="text-sm font-semibold text-ink-900">{noun} inactivos</h4>
        <span className="rounded-full bg-ink-050 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-ink-500">
          {rows.length}
        </span>
      </button>

      {open && (
        <ul className="border-t border-ink-050">
          {rows.map(({ product, usage }) => {
            const summary = scheduleSummary(product, usesCycles);
            const deletable = isUnused(usage);
            return (
              <li
                key={product.id}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-ink-050 px-4 py-2.5 last:border-b-0"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-medium text-ink-700">{product.name}</p>
                  <p className="text-[11.5px] text-ink-400">
                    {summary}
                    {!deletable && `${summary ? " · " : ""}${productUsageReason(usage)}`}
                  </p>
                </div>
                {/* Information only: proof its media survived. Editing them waits
                    until the product is reactivated. */}
                <ProductMediaIndicator productId={product.id} productName={product.name} />
                <button
                  type="button"
                  disabled={disabled || reactivating !== null}
                  onClick={async () => {
                    setReactivating(product.id);
                    await onReactivate(product);
                    setReactivating(null);
                  }}
                  className="rounded-lg border border-ink-200 px-2.5 py-1 text-[12px] font-medium text-ink-700 hover:bg-ink-050 disabled:opacity-40"
                >
                  {reactivating === product.id ? "Reactivando…" : "Reactivar"}
                </button>
                {deletable && (
                  <button
                    type="button"
                    disabled={disabled || deleteMutation.isPending}
                    onClick={() => setConfirming(product)}
                    className="rounded-lg border border-red-200 px-2.5 py-1 text-[12px] font-medium text-red-600 hover:bg-red-50 disabled:opacity-40"
                  >
                    Eliminar
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <AlertDialog open={confirming !== null} onOpenChange={(v: boolean) => !v && setConfirming(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar «{confirming?.name}»?</AlertDialogTitle>
            <AlertDialogDescription>
              Se borra para siempre, con sus medios. Solo se permite porque nada lo usa: ningún
              prospecto, pedido, promoción ni documento.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteMutation.isPending}
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={() => {
                if (confirming) deleteMutation.mutate(confirming.id);
                setConfirming(null);
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
